import { AgenticSSEEvent } from '#types/System.Agents.types';
import {
    AI_USAGE_LIMIT_EXCEEDED_EVENT,
    AiUsageLimitExceededError,
    isAiUsageLimitExceededPayload,
} from '#types/System.AiUsage.types';

const SSE_LIMIT_PREFIX = `event: ${AI_USAGE_LIMIT_EXCEEDED_EVENT}\ndata: `;

/**
 * Plain-text streaming reader used by one-shot. The cloud_run handler writes
 * raw text content but also writes an `aiUsageLimitExceeded` SSE frame on
 * mid-stream rate-limit exhaustion. We watch the tail of the buffer for that
 * exact prefix and surface it as a typed error instead of leaking SSE-shaped
 * bytes into the chat.
 */
export const readTextStream = async (
    reader: ReadableStreamDefaultReader<Uint8Array>,
    onData: (chunk: string) => void,
    onComplete?: () => void
): Promise<void> => {
    const decoder = new TextDecoder();
    let tail = '';

    const flush = (text: string, isFinal: boolean): void => {
        const combined = tail + text;
        const idx = combined.indexOf(SSE_LIMIT_PREFIX);
        if (idx === -1) {
            const keep = isFinal ? 0 : Math.min(combined.length, SSE_LIMIT_PREFIX.length);
            const emit = combined.slice(0, combined.length - keep);
            tail = combined.slice(combined.length - keep);
            if (emit.length > 0) onData(emit);
            return;
        }
        if (idx > 0) onData(combined.slice(0, idx));
        const after = combined.slice(idx + SSE_LIMIT_PREFIX.length);
        const end = after.indexOf('\n\n');
        const jsonText = end === -1 ? after : after.slice(0, end);
        try {
            const parsed = JSON.parse(jsonText);
            if (isAiUsageLimitExceededPayload(parsed)) {
                throw new AiUsageLimitExceededError({
                    window: parsed.window,
                    scope: parsed.scope,
                });
            }
        } catch (err) {
            if (err instanceof AiUsageLimitExceededError) throw err;
        }
        tail = '';
    };

    const read = async (): Promise<void> => {
        const { done, value } = await reader.read();
        if (done) {
            flush(decoder.decode(), true);
            onComplete?.();
            return;
        }
        flush(decoder.decode(value, { stream: true }), false);
        return read();
    };

    await read();
};

export type ReadSSEStreamOptions = {
    idleTimeoutMs?: number;
    onIdleTimeout?: () => void;
};

/**
 * Reads an agentic SSE stream. When `idleTimeoutMs` is set, each `reader.read()`
 * races against a watchdog timer that resets on every received chunk (including
 * the server's `: ping` comment heartbeats). On expiry the reader is cancelled,
 * `onIdleTimeout` is invoked to abort the underlying fetch (which closes the
 * connection and so cancels the server's agentic loop via its `res.on('close')`
 * handler), and the promise rejects with `Stream idle timeout`. That rejection
 * settles first, so it wins over the `AbortError` the aborted fetch produces;
 * `callRunLanguageModel`'s catch then routes it to `onError` (not the graceful
 * abort path), clearing the stuck spinner. Comment frames carry no `event:`/
 * `data:` lines and are skipped by `processBuffer`.
 */
export const readSSEStream = async (
    reader: ReadableStreamDefaultReader<Uint8Array>,
    onEvent: (event: AgenticSSEEvent) => void,
    onComplete?: () => void,
    options?: ReadSSEStreamOptions
): Promise<void> => {
    const { idleTimeoutMs, onIdleTimeout } = options ?? {};
    const decoder = new TextDecoder();
    let buffer = '';
    let idleTimer: ReturnType<typeof setTimeout> | undefined;

    const readChunk = (): Promise<ReadableStreamReadResult<Uint8Array>> => {
        if (idleTimeoutMs === undefined) return reader.read();
        return new Promise((resolve, reject) => {
            idleTimer = setTimeout(() => {
                reader.cancel().catch(() => {});
                onIdleTimeout?.();
                reject(new Error('Stream idle timeout'));
            }, idleTimeoutMs);
            reader.read().then(resolve, reject);
        });
    };

    const processBuffer = () => {
        const frames = buffer.split('\n\n');
        buffer = frames.pop() || '';

        for (const frame of frames) {
            if (frame.trim().length === 0) continue;

            let eventType = '';
            let eventData = '';

            for (const line of frame.split('\n')) {
                if (line.startsWith('event: ')) {
                    eventType = line.slice(7);
                } else if (line.startsWith('data: ')) {
                    eventData = line.slice(6);
                }
            }

            if (eventType && eventData) {
                try {
                    const parsed = JSON.parse(eventData);
                    if (eventType === AI_USAGE_LIMIT_EXCEEDED_EVENT && isAiUsageLimitExceededPayload(parsed)) {
                        throw new AiUsageLimitExceededError({
                            window: parsed.window,
                            scope: parsed.scope,
                        });
                    }
                    onEvent({ type: eventType, data: parsed } as AgenticSSEEvent);
                } catch (err) {
                    if (err instanceof AiUsageLimitExceededError) throw err;
                    console.warn('Failed to parse SSE event data:', eventData);
                }
            }
        }
    };

    const read = async (): Promise<void> => {
        const { done, value } = await readChunk();
        if (idleTimer !== undefined) clearTimeout(idleTimer);

        if (done) {
            if (buffer.trim().length > 0) {
                buffer += '\n\n';
                processBuffer();
            }
            onComplete?.();
            return;
        }

        buffer += decoder.decode(value, { stream: true });
        processBuffer();
        return read();
    };

    try {
        await read();
    } finally {
        if (idleTimer !== undefined) clearTimeout(idleTimer);
    }
};
